import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { Champ, Etiquette, Selection } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { JournalAudit } from '../../components/ndf/JournalAudit';

/**
 * Pilotage des notes de frais (specs G4).
 *
 * L'écran s'ouvre sur CE QUI ATTEND UNE DÉCISION, pas sur les totaux : c'est
 * la seule chose sur laquelle on peut agir aujourd'hui. Les cumuls, eux,
 * décrivent le passé — utiles, mais on ne les regarde pas tous les matins.
 */
export function NdfPilotagePage() {
  const annees = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i);
  const [annee, setAnnee] = useState(annees[0]!);
  const [onglet, setOnglet] = useState<'pilotage' | 'audit'>('pilotage');

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'pilotage', annee],
    queryFn: () => ndfApi.historique.pilotage(annee),
    retry: false,
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="font-titre text-lg font-bold text-nuit">Pilotage des notes de frais</h1>
        <div className="flex gap-1 rounded-lg border border-nuit/15 p-1">
          {(['pilotage', 'audit'] as const).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => setOnglet(o)}
              className={`min-h-11 rounded-md px-3 text-sm ${
                onglet === o ? 'bg-nuit font-semibold text-white' : 'text-nuit'
              }`}
            >
              {o === 'pilotage' ? 'Chiffres' : 'Journal d’audit'}
            </button>
          ))}
        </div>
      </header>

      {onglet === 'audit' ? (
        <JournalAudit />
      ) : (
        <>
          <Champ label="Année">
            <Selection value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
              {annees.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Selection>
          </Champ>

          {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
          {error && <MessageErreur erreur={error} />}

          {data && (
            <>
              {/* En premier : ce qui bloque quelqu'un aujourd'hui. */}
              <section className="rounded-lg border border-nuit/10 bg-white p-4">
                <h2 className="mb-2 font-titre text-sm font-bold text-nuit">
                  En attente de décision ({data.en_attente.length})
                </h2>
                {data.en_attente.length === 0 ? (
                  <p className="text-sm text-nuit/70">Rien n’attend de décision.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.en_attente.map((n) => (
                      <li key={n.numero} className="flex flex-wrap justify-between gap-2">
                        <span className="text-nuit">
                          {n.numero} : {n.salarie}, chez {n.chez}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-medium text-nuit">{euros(n.montant)}</span>
                          {/* L'ancienneté est ce qui distingue un retard d'une
                              file normale : au-delà de cinq jours, quelqu'un
                              attend son argent. */}
                          <Etiquette ton={n.jours >= 5 ? 'alerte' : 'neutre'}>
                            {n.jours} jour{n.jours > 1 ? 's' : ''}
                          </Etiquette>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="rounded-lg border border-nuit/10 bg-white p-4">
                <h2 className="mb-2 font-titre text-sm font-bold text-nuit">
                  Cumuls kilométriques {data.annee}
                </h2>
                {data.cumuls_ik.length === 0 ? (
                  <p className="text-sm text-nuit/70">Aucun trajet déclaré.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.cumuls_ik.map((c) => (
                      <li key={c.salarie} className="flex justify-between gap-2">
                        <span className="text-nuit">{c.salarie}</span>
                        <span className="flex items-center gap-2">
                          <span className="font-medium text-nuit">{c.km} km</span>
                          {c.alerte && (
                            <Etiquette ton="attention">au-delà de {data.seuil_ik} km</Etiquette>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <Tableau titre={`Total ${data.annee} : ${euros(data.total_annee)}`} lignes={data.par_mois} />
              <Tableau titre="Par salarié" lignes={data.par_salarie} />
              <Tableau titre="Par catégorie" lignes={data.par_categorie} />
              <Tableau titre="Refacturable par client et mission" lignes={data.refacturable} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function Tableau({
  titre,
  lignes,
}: {
  titre: string;
  lignes: Array<{ libelle: string; montant: number; nombre: number }>;
}) {
  return (
    <section className="rounded-lg border border-nuit/10 bg-white p-4">
      <h2 className="mb-2 font-titre text-sm font-bold text-nuit">{titre}</h2>
      {lignes.length === 0 ? (
        <p className="text-sm text-nuit/70">Rien à afficher.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {lignes.map((l) => (
            <li key={l.libelle} className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
              <span className="text-nuit/70">
                {l.libelle} <span className="text-nuit/40">({l.nombre})</span>
              </span>
              <span className="font-medium text-nuit">{euros(l.montant)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
